import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { devicesAPI } from '../../api/api';
import { updateDevice } from '../../redux/slices/devicesSlice';
import { useEntityDetails } from '../../context/EntityDetailsContext';
import { useLanguage } from '../../context/LanguageContext';
import { buildReportId } from '../../utils/reportId';

const statusColors = {
  test: 'bg-gray-100 text-gray-800',
  'warten auf Gerät': 'bg-gray-100 text-gray-800',
  'bereit zur Analyse': 'bg-blue-100 text-blue-800',
  'warten auf KV': 'bg-red-100 text-red-800',
  'warten auf Ersatzteil': 'bg-red-100 text-red-800',
  'in Reparatur': 'bg-blue-100 text-blue-800',
  'zu verrechnen': 'bg-yellow-100 text-yellow-800',
  abgeschlossen: 'bg-green-100 text-green-800',
};

const DeviceDetails = ({ data, onRefetch }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { close } = useEntityDetails();
  const { t } = useLanguage();
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    devicesAPI
      .getRepairs(data.id)
      .then((res) => {
        if (!cancelled) setRepairs(res.data.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [data.id]);

  const startEdit = () => {
    setForm({
      serial_number: data.serial_number || '',
      brand: data.brand || '',
      model: data.model || '',
    });
    setIsEditing(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    dispatch(updateDevice({ id: data.id, data: form }))
      .unwrap()
      .then(() => {
        setIsEditing(false);
        onRefetch?.();
      })
      .catch((err) => console.error('Failed to save device:', err));
  };

  return (
    <div className='space-y-6'>
      <div className='bg-gray-50 rounded-lg p-4'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900'>{t('deviceDetails.sectionTitle')}</h3>
          {!isEditing && (
            <button onClick={startEdit} className='btn-secondary text-sm'>
              {t('common.edit')}
            </button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSave} className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('devices.serialNumber')} *</label>
              <input
                type='text'
                value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                className='input-field'
                required
              />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('devices.brand')}</label>
                <input
                  type='text'
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  className='input-field'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('devices.model')}</label>
                <input
                  type='text'
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className='input-field'
                />
              </div>
            </div>
            <div className='flex justify-end space-x-3 pt-2'>
              <button type='button' onClick={() => setIsEditing(false)} className='btn-secondary'>
                {t('common.cancel')}
              </button>
              <button type='submit' className='btn-primary'>
                {t('devices.saveChanges')}
              </button>
            </div>
          </form>
        ) : (
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <p className='text-sm text-gray-500'>{t('devices.serialNumber')}</p>
              <p className='font-medium text-gray-900'>{data.serial_number}</p>
            </div>
            <div>
              <p className='text-sm text-gray-500'>{t('deviceDetails.brandModel')}</p>
              <p className='font-medium text-gray-900'>
                {[data.brand, data.model].filter(Boolean).join(' ') || '-'}
              </p>
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className='text-lg font-semibold text-gray-900 mb-4'>
          {t('deviceDetails.repairHistory')}
        </h3>
        {loading ? (
          <div className='text-center py-8 text-gray-500'>{t('deviceDetails.loadingHistory')}</div>
        ) : repairs.length === 0 ? (
          <div className='text-center py-8 bg-gray-50 rounded-lg'>
            <p className='text-gray-500'>{t('deviceDetails.noRepairsYet')}</p>
          </div>
        ) : (
          <div className='space-y-3'>
            {repairs.map((r) => (
              <button
                key={r.id}
                type='button'
                onClick={() => {
                  close();
                  navigate(`/repairs/${r.id}`);
                }}
                className='block w-full text-left border rounded-lg p-3 hover:bg-gray-50 hover:border-blue-300 transition-colors'
              >
                <div className='flex items-center justify-between'>
                  <span className='font-semibold text-blue-600'>{buildReportId(r.id, r.created_at)}</span>
                  <span
                    className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${statusColors[r.status] || 'bg-gray-100 text-gray-800'}`}
                  >
                    {r.status}
                  </span>
                </div>
                <p className='text-sm text-gray-500 mt-1'>
                  {r.customer_name || r.dealer_name || t('deviceDetails.noCustomerOrDealer')} &middot;{' '}
                  {new Date(r.created_at).toLocaleDateString('en-GB')}
                </p>
                {r.problem_description && (
                  <p className='text-sm text-gray-700 mt-1'>{r.problem_description}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceDetails;
